// =============================================================================
// domain/range.ts — the global date-range filter, resolved to bounds.
// -----------------------------------------------------------------------------
// The scope a user actually SETS and sees. Every tab respects it, and it is the
// answer to "what am I looking at" — not the loader's 45-day boot window, which
// is plumbing and should be invisible during normal use (D57).
//
// Pure: `today` is injected, never read from the clock (D26). Reading it here
// would make the whole filter untestable and would re-bucket every screen at
// midnight under a user who has not touched anything.
//
// ── ⚠ SESSION MODE CHANGES THREE PRESETS AND ONLY THREE ──
//
// A CME "day" runs 18:00 ET the previous evening to 17:00 ET. Under
// `dayBoundaryMode: 'session'` that moves the anchor for `today`, `yesterday`
// and `week` — the three presets whose bounds are relative to now. `month`,
// `year`, `custom` and `all` are absolute calendar spans and are identical in
// both modes.
//
// The old client says this in as many words, and it is worth restating rather
// than compressing: a port that "helpfully" applied session-awareness to `month`
// would shift every monthly total by a day at the boundary.
//
// ⚠ `week` differs by ENDPOINT too, not just anchor. Calendar mode is the full
// ISO week Mon→Sun; session mode is Mon→Fri, because there are no Saturday or
// Sunday sessions and including them would leave two dead columns implying the
// trader was flat when the market was shut.
// =============================================================================

import { previousSessionDate } from './session';

/**
 * ⚠ A VALUE, with the type derived from it — not a bare type union.
 *
 * Anything that reads a preset from OUTSIDE the program (localStorage, a URL,
 * a stale saved view) has to check it, and a type union cannot be checked at
 * runtime. Without this list the check degrades to `typeof x === 'string'`,
 * which admits anything — and an unrecognised preset falls through `rangeBounds`
 * to `all`, so the failure is a SILENTLY WIDENED range rather than an error:
 * the whole book, under a label claiming this week.
 *
 * Order matches the old client's DATE_RANGE_PRESETS, so the picker can render
 * straight from it.
 */
export const RANGE_PRESETS = [
  'all', 'today', 'yesterday', 'week', 'month', 'year', 'custom',
] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number];

export function isRangePreset(v: unknown): v is RangePreset {
  return typeof v === 'string' && (RANGE_PRESETS as readonly string[]).includes(v);
}

export type DayBoundaryMode = 'calendar' | 'session';

export interface RangeSelection {
  preset: RangePreset;
  customStart?: string;
  customEnd?: string;
}

export interface RangeBounds {
  /** Inclusive YYYY-MM-DD, or null for "no lower bound" (preset `all`). */
  startDate: string | null;
  /** Inclusive YYYY-MM-DD, or null for "no upper bound". */
  endDate: string | null;
}

/** The default, matching the old client's `activeDateRange`. */
export const DEFAULT_RANGE: RangeSelection = { preset: 'week' };

// ── date helpers, all UTC and all string-in/string-out ───────────────────────
// Deliberately not Date-based arithmetic in local time: a user in UTC+13 doing
// `new Date()` arithmetic on a YYYY-MM-DD lands on the wrong day, and every
// bound here is compared against `date` strings that carry no zone.

const toParts = (iso: string): [number, number, number] => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return [y ?? 1970, m ?? 1, d ?? 1];
};

const fromUtc = (d: Date): string => d.toISOString().slice(0, 10);

const utc = (iso: string): Date => {
  const [y, m, d] = toParts(iso);
  return new Date(Date.UTC(y, m - 1, d));
};

export function addDays(iso: string, n: number): string {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return fromUtc(d);
}

/** 0 = Sunday … 6 = Saturday, for a bare date string. */
export function dayOfWeek(iso: string): number {
  return utc(iso).getUTCDay();
}

/**
 * Resolve a selection to inclusive bounds.
 *
 * `today` is the ANCHOR — in session mode the caller passes the current session
 * date rather than the calendar date, which is what makes the three relative
 * presets session-aware without this module knowing the time.
 */
export function rangeBounds(
  sel: RangeSelection,
  today: string,
  mode: DayBoundaryMode = 'calendar',
): RangeBounds {
  const session = mode === 'session';

  switch (sel.preset) {
    case 'today':
      return { startDate: today, endDate: today };

    case 'yesterday': {
      // ⚠ SESSION MODE SKIPS THE WEEKEND — `previousSessionDate`, not −1 day.
      //
      // The first version of this stepped back one day in both modes, with a
      // confident comment arguing that skipping "would relabel Friday's session
      // as yesterday on a Monday". That is precisely what the old client does
      // and precisely what the word means to a futures trader: there is no
      // Sunday session, so "yesterday" on a Monday is Friday. The −1 version
      // returned an empty screen every Monday.
      //
      // Its test could not catch it — the assertion ran on a Wednesday, where
      // both rules give the same answer. Same shape as every other defect in
      // this port: a true assertion about a case that cannot expose the bug.
      const y = session ? previousSessionDate(today) : addDays(today, -1);
      return { startDate: y, endDate: y };
    }

    case 'week': {
      const dow = dayOfWeek(today);
      const toMonday = dow === 0 ? -6 : 1 - dow;
      const mon = addDays(today, toMonday);
      // Session mode stops at Friday: there are no weekend sessions, and two
      // empty columns read as "flat" rather than "closed".
      return { startDate: mon, endDate: addDays(mon, session ? 4 : 6) };
    }

    case 'month': {
      const [y, m] = toParts(today);
      const first = `${y}-${String(m).padStart(2, '0')}-01`;
      const last = fromUtc(new Date(Date.UTC(y, m, 0)));
      return { startDate: first, endDate: last };
    }

    case 'year': {
      const [y] = toParts(today);
      return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
    }

    case 'custom': {
      // An incomplete custom range is UNBOUNDED on the missing side rather than
      // empty. A half-filled date picker showing "no trades" reads as a data
      // problem; showing everything from the one date given reads as a filter
      // half-applied, which is what it is.
      const a = sel.customStart || null;
      const b = sel.customEnd || null;
      // Reversed inputs are swapped, not treated as an empty span. Typing the
      // end date first is ordinary, and an empty result mid-edit looks broken.
      if (a && b && a > b) return { startDate: b, endDate: a };
      return { startDate: a, endDate: b };
    }

    case 'all':
    default:
      return { startDate: null, endDate: null };
  }
}

/** Human label, matching the old client's wording. */
export function rangeLabel(sel: RangeSelection): string {
  switch (sel.preset) {
    case 'today': return 'Today';
    case 'yesterday': return 'Yesterday';
    case 'week': return 'This week';
    case 'month': return 'This month';
    case 'year': return 'This year';
    case 'all': return 'All time';
    case 'custom': {
      const a = sel.customStart, b = sel.customEnd;
      if (a && b) return a === b ? a : `${a} → ${b}`;
      if (a) return `From ${a}`;
      if (b) return `Until ${b}`;
      return 'Custom';
    }
    default: return 'This week';
  }
}

/**
 * The same label with the dates spelled out — for anything that LEAVES the app.
 *
 * 🔴 WHY IT EXISTS. The shared PNG was titled "This week". That is true for six
 * days and then silently false, and two reports saved a month apart are
 * indistinguishable. A custom range was worse: raw ISO, "2026-05-01 → 2026-05-16".
 * The picture is the one artefact designed to be posted somewhere and read
 * later, so it is the one place the range has to be self-describing. #43.
 *
 * Beta's `activeDateRangeLabelHuman` (app/index.html:15853) transcribed:
 *
 *   All time                              — no dates; there are none to give
 *   This week (May 11 – May 17, 2026)
 *   Today (May 17, 2026)                  — one day, stated once
 *   Custom (May 1 – May 16, 2026)
 *
 * ⚠ THE YEAR COMES FROM THE END DATE, singular, even when the span crosses New
 * Year. Beta does that and it is the readable choice: "Dec 29 – Jan 4, 2027"
 * says what a reader needs without a second year cluttering the title bar.
 *
 * ⚠ TAKES `today` AND `mode` rather than reading a clock, so a snapshot title is
 * testable and so a session-mode week resolves to the same dates the numbers
 * beneath it were computed from.
 */
export function rangeLabelHuman(
  sel: RangeSelection,
  today: string,
  mode: DayBoundaryMode = 'calendar',
): string {
  const base = rangeLabel(sel);
  if (sel.preset === 'all') return base;

  const b = rangeBounds(sel, today, mode);
  const start = b.startDate;
  const end = b.endDate;
  // ⚠ A half-open custom range has nothing to spell out — "From 2026-05-01" is
  // already self-describing, so it is returned as it stands rather than
  // decorated with a fabricated end.
  if (!start || !end) return base;

  const md = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const year = new Date(`${end}T00:00:00`).getFullYear();
  if (Number.isNaN(year)) return base;

  const span = start === end ? `${md(start)}, ${year}` : `${md(start)} – ${md(end)}, ${year}`;
  // ⚠ For a custom range `rangeLabel` returns the raw ISO span, not the word
  // "Custom" — so the prefix is taken from the preset, not from that.
  const prefix = sel.preset === 'custom' ? 'Custom' : base;
  return `${prefix} (${span})`;
}

/** Inclusive on both ends; a null bound does not constrain. */
export function dateInBounds(date: string, b: RangeBounds): boolean {
  if (!date) return false;
  const d = date.slice(0, 10);
  if (b.startDate && d < b.startDate) return false;
  if (b.endDate && d > b.endDate) return false;
  return true;
}
